import { formatDateTime } from "@/api/datetime";
import { useDialog } from "@/components/DialogProvider";
import { InviteUserDialog } from "@/components/InviteUserDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlacesAutocomplete } from "@/components/ui/places-autocomplete";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import UserAuditLogs from "@/components/UserAuditLogs";
import { useAgencies } from "@/features/agencies/hooks/useAgencies";
import { useCustomer, useCustomerOperations } from "@/features/customers/hooks/useCustomers";
import { useCustomerProjects, useProjects } from "@/features/projects/hooks/useProjects";
import { useCustomerInvite } from "@/features/users/hooks/useCustomerInvite";
import { useUsers } from "@/features/users/hooks/useUsers";
import { UsersService } from "@/features/users/users-service";
import { storage } from "@/firebase";
import { getJobLocationAddress, getJobLocationNotes } from "@/lib/jobLocation";
import { formatPhoneNumber } from "@/lib/utils";
import ProjectsTable from "@/routes/dashboard/admin/projects/-components/ProjectsTable";
import * as Sentry from "@sentry/react";
import type { CustomerPhoneNumber, PhoneNumberType } from "@shared/types/firestore";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { Timestamp } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  ArrowLeft,
  Ban,
  Download,
  Edit2,
  FileText,
  Mail,
  MapPin,
  Phone,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { UpdatePasswordDialog } from "./-UpdatePasswordDialog";

// Generate a unique ID
const generateUniqueId = () => {
  return crypto.randomUUID();
};

type PhoneFormValue = {
  number: string;
  type: PhoneNumberType | "";
  extension: string;
};

const PHONE_TYPE_OPTIONS: { value: PhoneNumberType; label: string }[] = [
  { value: "work", label: "Work" },
  { value: "cell", label: "Cell" },
  { value: "home", label: "Home" },
  { value: "fax", label: "Fax" },
  { value: "other", label: "Other" },
];

const PHONE_TYPE_LABELS: Record<PhoneNumberType, string> = {
  work: "Work",
  cell: "Cell",
  home: "Home",
  fax: "Fax",
  other: "Other",
};

const createEmptyPhone = (): PhoneFormValue => ({
  number: "",
  type: "",
  extension: "",
});

const toPhoneFormValue = (phone?: CustomerPhoneNumber | string | null): PhoneFormValue => {
  if (!phone) {
    return createEmptyPhone();
  }

  if (typeof phone === "string") {
    return {
      number: phone,
      type: "",
      extension: "",
    };
  }

  return {
    number: phone.number ?? "",
    type: phone.type ?? "",
    extension: phone.extension ?? "",
  };
};

const formatDate = (timestamp: Timestamp | Date | undefined | null) => {
  if (!timestamp) return "N/A";
  if (timestamp instanceof Timestamp) {
    return formatDateTime(timestamp.toDate());
  } else if (timestamp instanceof Date) {
    return formatDateTime(timestamp);
  }
  return "N/A";
};

export const Route = createFileRoute("/dashboard/admin/customers/$customerId/")({
  head: ({ params }) => ({
    meta: [
      {
        title: `Customer ${params.customerId} - GetHandle Admin`,
      },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { customerId } = Route.useParams();
  const { customer, loading, error } = useCustomer(customerId);
  const { inviteData } = useCustomerInvite(customer?.email);
  const { confirm } = useDialog();
  const navigate = useNavigate();
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useCustomerProjects(customerId);
  const { updateProject, updating, projects: allProjects } = useProjects();
  // that are not assigned to any customer
  const unassignedProjects = allProjects.filter((p) => !p.customerId);
  const { users } = useUsers();
  const { agencies } = useAgencies();
  const {
    updateCustomer,
    updateCustomerPassword,
    updateCustomerEmail,
    deleteCustomer,
    addAttachment,
    removeAttachment,
    addContact,
    updateContact,
    removeContact,
    loading: operationLoading,
    error: operationError,
  } = useCustomerOperations();

  const router = useRouter();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isAssignProjectDialogOpen, setIsAssignProjectDialogOpen] = useState(false);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [notesFormData, setNotesFormData] = useState("");
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    address: {
      street: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    emails: [""],
    phoneNumbers: [createEmptyPhone()],
    notes: "",
    password: "",
    confirmPassword: "",
    website: "",
    bestCommunicationMethod: "" as "" | "call" | "text" | "whatsapp" | "email",
    firstName: "",
    lastName: "",
    title: "",
  });
  const [contactFormData, setContactFormData] = useState({
    id: "",
    title: "",
    name: "",
    email: "",
    phone: createEmptyPhone(),
    notes: "",
  });
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

  // Filter users for project form
  const customerUsers = users.filter((user) => user.type === "customer");
  const managerUsers = users.filter((user) => user.type === "manager");
  const adminUsers = users.filter((user) => user.type === "admin");

  const handleAssignProject = async (projectId: string, projectName: string) => {
    if (!customer) return;

    const customerDisplayName =
      customer.name ||
      [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
      "this customer";

    const confirmed = await confirm({
      description: `Are you sure you want to assign "${projectName}" to ${customerDisplayName}?`,
    });

    if (!confirmed) return;

    try {
      await updateProject(projectId, {
        customerId: customer.uid,
      });
      toast.success(`Project "${projectName}" assigned to ${customerDisplayName}`);
    } catch (error) {
      console.error("Error assigning project:", error);
      toast.error("Failed to assign project");
    }
  };

  const handleEditCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer) return;

    const companyName = editFormData.name.trim();
    const firstName = editFormData.firstName.trim();
    const lastName = editFormData.lastName.trim();
    const newPrimaryEmail = editFormData.email.trim().toLowerCase();
    const currentEmail = (customer.email || "").trim().toLowerCase();

    try {
      // Require at least company name OR contact person name
      if (!companyName && !firstName && !lastName) {
        toast.error("Provide at least a company name or contact person name (first/last name).");
        return;
      }

      // Handle primary email update if changed
      if (newPrimaryEmail && newPrimaryEmail !== currentEmail) {
        try {
          await updateCustomerEmail(customer.uid, newPrimaryEmail);
        } catch (emailError: any) {
          toast.error(`Failed to update email: ${emailError.message || "Unknown error"}`);
          return;
        }
      }

      // Handle password update for authless customers
      if (customer.authless && editFormData.password) {
        if (editFormData.password !== editFormData.confirmPassword) {
          toast.error("Passwords do not match");
          return;
        }
        if (editFormData.password.length < 6) {
          toast.error("Password must be at least 6 characters");
          return;
        }

        await updateCustomerPassword(customer.uid, editFormData.password);
        toast.success("Customer password set successfully! Customer can now log in.");
      }

      const sanitizedPhoneDetails = editFormData.phoneNumbers.reduce<CustomerPhoneNumber[]>(
        (acc, phone) => {
          const number = phone.number.trim();
          if (!number) {
            return acc;
          }

          acc.push({
            number,
            type: (phone.type || "work") as PhoneNumberType,
            extension: phone.extension.trim() ? phone.extension.trim() : null,
          });

          return acc;
        },
        [],
      );

      // Build emails array: only additional emails (primary email is separate)
      const additionalEmails = editFormData.emails
        .map((email) => email.trim())
        .filter((email) => email !== "");

      const updates: any = {
        emails: additionalEmails,
        phoneNumbers: sanitizedPhoneDetails.map((phone) => phone.number),
        phoneNumberDetails: sanitizedPhoneDetails,
        // Always include these fields to allow clearing them - keep them separate!
        name: companyName || null,
        firstName: firstName || null,
        lastName: lastName || null,
        title: editFormData.title.trim() || null,
        notes: editFormData.notes?.trim() || null,
        website: editFormData.website?.trim() || null,
        bestCommunicationMethod: editFormData.bestCommunicationMethod?.trim() || null,
      };

      // Only include address if it has values, otherwise set to null
      if (Object.values(editFormData.address).some((val) => val.trim() !== "")) {
        updates.address = editFormData.address;
      } else {
        updates.address = null;
      }

      const result = await updateCustomer(customer.uid, updates);
      if (result._tag === "Right") {
        setIsEditDialogOpen(false);
      } else {
        toast.error(`Error updating customer: ${result.left.message}`);
        console.error("Error updating customer:", result.left);
      }
    } catch (error) {
      console.error("Error updating customer:", error);
      toast.error("Failed to update customer");
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !customer) return;

    const file = files[0];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSize) {
      toast.info("File size must be less than 10MB");
      return;
    }

    const fileName = `${Date.now()}-${file.name}`;
    setUploadingFiles((prev) => [...prev, fileName]);

    try {
      const storageRef = ref(storage, `customers/${customer.uid}/attachments/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const newAttachment = {
        id: fileName,
        uniqueId: generateUniqueId(),
        label: file.name,
        fileName: file.name,
        fileType: file.type,
        fileUrl: downloadURL,
        uploadedAt: new Date(),
        fileSize: file.size,
      };

      await addAttachment(customer.uid, newAttachment);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.info("Failed to upload file");
    } finally {
      setUploadingFiles((prev) => prev.filter((f) => f !== fileName));
      // Reset file input
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleRemoveAttachment = async (attachmentId: string, fileUrl?: string) => {
    if (!customer) return;
    if (!(await confirm({ description: "Are you sure you want to delete this attachment?" })))
      return;

    try {
      // Delete from Firebase Storage if fileUrl is provided
      if (fileUrl) {
        try {
          const storageRef = ref(storage, fileUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn("Could not delete file from storage:", storageError);
        }
      }

      await removeAttachment(customer.uid, attachmentId);
    } catch (error) {
      console.error("Error removing attachment:", error);
      toast.info("Failed to remove attachment");
    }
  };

  const openEditDialog = () => {
    if (!customer) return;

    const normalizedPhoneNumbers =
      customer.phoneNumberDetails && customer.phoneNumberDetails.length > 0
        ? customer.phoneNumberDetails.map((phone) => toPhoneFormValue(phone))
        : customer.phoneNumbers && customer.phoneNumbers.length > 0
          ? customer.phoneNumbers.map((phone) => toPhoneFormValue(phone))
          : [createEmptyPhone()];

    // Filter out primary email from additional emails
    const additionalEmails =
      customer.emails && customer.emails.length > 0
        ? customer.emails.filter((email) => email !== customer.email)
        : [];

    setEditFormData({
      name: customer.name || "",
      email: customer.email || "",
      address: {
        street: customer.address?.street || "",
        address2: customer.address?.address2 || "",
        city: customer.address?.city || "",
        state: customer.address?.state || "",
        zipCode: customer.address?.zipCode || "",
        country: customer.address?.country || "",
      },
      emails: additionalEmails,
      phoneNumbers: normalizedPhoneNumbers,
      notes: customer.notes || "",
      password: "",
      confirmPassword: "",
      website: customer.website || "",
      bestCommunicationMethod:
        customer.bestCommunicationMethod || ("" as "" | "call" | "text" | "whatsapp" | "email"),
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      title: customer.title || "",
    });
    setIsEditDialogOpen(true);
  };

  const openContactDialog = (contact?: any) => {
    if (contact) {
      setContactFormData({
        id: contact.uniqueId || contact.id || "",
        title: contact.title || "",
        name: contact.name || "",
        email: contact.email || "",
        phone: toPhoneFormValue(contact.phoneDetails ?? contact.phone),
        notes: contact.notes || "",
      });
    } else {
      setContactFormData({
        id: "",
        title: "",
        name: "",
        email: "",
        phone: createEmptyPhone(),
        notes: "",
      });
    }
    setIsContactDialogOpen(true);
  };

  const handleSaveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer) return;

    // Validate that name is provided when adding a new contact
    if (!contactFormData.id && !contactFormData.name.trim()) {
      toast.error("Contact name is required");
      return;
    }

    try {
      const sanitizedPhoneDetails = contactFormData.phone.number.trim()
        ? {
            number: contactFormData.phone.number.trim(),
            type: (contactFormData.phone.type || "work") as PhoneNumberType,
            extension: contactFormData.phone.extension.trim()
              ? contactFormData.phone.extension.trim()
              : null,
          }
        : null;

      const isUpdate = !!contactFormData.id;

      Sentry.addBreadcrumb({
        category: "customer-contact",
        message: isUpdate ? "Updating customer contact" : "Adding customer contact",
        level: "info",
        data: {
          customerId: customer.uid,
          customerName: customer.name,
          isUpdate,
          hasPhone: !!sanitizedPhoneDetails?.number,
          hasEmail: !!contactFormData.email,
          contactId: contactFormData.id,
        },
      });

      if (isUpdate) {
        // For updates, allow any field to be null
        const payload = {
          title: contactFormData.title.trim() || null,
          name: contactFormData.name.trim() || null,
          email: contactFormData.email.trim() || null,
          phone: sanitizedPhoneDetails ? sanitizedPhoneDetails.number : null,
          notes: contactFormData.notes.trim() || null,
          phoneDetails: sanitizedPhoneDetails,
        };
        const result = await updateContact(customer.uid, contactFormData.id, payload);
        if (!result.success) {
          const errorMsg = result.error?.message || "Failed to update contact";
          toast.error(errorMsg);
          Sentry.captureException(result.error || new Error(errorMsg), {
            tags: {
              operation: "update-contact",
              customerId: customer.uid,
              contactId: contactFormData.id,
            },
            extra: {
              customerName: customer.name,
              payload: { ...payload, phone: sanitizedPhoneDetails?.number ? "***" : null },
            },
          });
          return;
        }
        toast.success("Contact updated successfully");
      } else {
        // For new contacts, name is required
        const payload = {
          title: contactFormData.title.trim() || null,
          name: contactFormData.name.trim(),
          email: contactFormData.email.trim() || null,
          phone: sanitizedPhoneDetails ? sanitizedPhoneDetails.number : null,
          notes: contactFormData.notes.trim() || null,
          phoneDetails: sanitizedPhoneDetails,
        };
        const result = await addContact(customer.uid, payload);
        if (!result.success) {
          const errorMsg = result.error?.message || "Failed to add contact";
          toast.error(errorMsg);
          Sentry.captureException(result.error || new Error(errorMsg), {
            tags: {
              operation: "add-contact",
              customerId: customer.uid,
            },
            extra: {
              customerName: customer.name,
              payload: { ...payload, phone: sanitizedPhoneDetails?.number ? "***" : null },
            },
          });
          return;
        }
        toast.success("Contact added successfully");
      }
      setIsContactDialogOpen(false);
    } catch (error) {
      console.error("Error saving contact:", error);
      Sentry.captureException(error, {
        tags: {
          operation: contactFormData.id ? "update-contact" : "add-contact",
          customerId: customer?.uid,
        },
      });
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleRemoveContact = async (contactId: string) => {
    if (!customer) return;
    if (!(await confirm({ description: "Are you sure you want to delete this contact?" }))) return;

    try {
      Sentry.addBreadcrumb({
        category: "customer-contact",
        message: "Removing customer contact",
        level: "info",
        data: {
          customerId: customer.uid,
          customerName: customer.name,
          contactId,
        },
      });

      const result = await removeContact(customer.uid, contactId);
      if (!result.success) {
        const errorMsg = result.error?.message || "Failed to remove contact";
        toast.error(errorMsg);
        Sentry.captureException(result.error || new Error(errorMsg), {
          tags: {
            operation: "remove-contact",
            customerId: customer.uid,
            contactId,
          },
          extra: {
            customerName: customer.name,
          },
        });
        return;
      }
      toast.success("Contact removed successfully");
    } catch (error) {
      console.error("Error removing contact:", error);
      Sentry.captureException(error, {
        tags: {
          operation: "remove-contact",
          customerId: customer?.uid,
        },
      });
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleSaveNotes = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer) return;

    try {
      const updates: any = {
        notes: notesFormData.trim() || null,
      };

      const result = await updateCustomer(customer.uid, updates);
      if (result._tag === "Right") {
        setIsNotesDialogOpen(false);
        toast.success("Notes updated successfully");
      } else {
        toast.error(`Error updating notes: ${result.left.message}`);
        console.error("Error updating notes:", result.left);
      }
    } catch (error) {
      console.error("Error updating notes:", error);
      toast.error("Failed to update notes");
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customer) return;

    const customerDisplayName =
      customer.name ||
      [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
      "this customer";

    const confirmed = await confirm({
      title: "Delete Customer",
      description: `Are you sure you want to delete ${customerDisplayName}? This action cannot be undone and will permanently delete the customer and all their data.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      await deleteCustomer(customer.uid);
      toast.success(`Customer "${customerDisplayName}" has been deleted successfully`);
      navigate({ to: "/dashboard/admin/customers" });
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    }
  };

  const handleToggleAccessRevoked = async () => {
    if (!customer || customer.authless) return;

    const isRevoking = !customer.accessRevoked;
    const customerDisplayName =
      customer.name ||
      [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
      "this customer";

    const confirmed = await confirm({
      title: isRevoking ? "Revoke Access" : "Restore Access",
      description: isRevoking
        ? `Are you sure you want to revoke login access for ${customerDisplayName}? They will be immediately signed out and unable to sign in.`
        : `Are you sure you want to restore login access for ${customerDisplayName}?`,
      confirmText: isRevoking ? "Revoke" : "Restore",
      cancelText: "Cancel",
    });
    if (!confirmed) return;

    try {
      await UsersService.toggleAccess(customer.uid, isRevoking);
      toast.success(isRevoking ? "Access revoked successfully" : "Access restored successfully");
    } catch (error: any) {
      toast.error(error.message || `Failed to ${isRevoking ? "revoke" : "restore"} access`);
      console.error("Error toggling access:", error);
    }
  };

  const addEmailField = () => {
    setEditFormData((prev) => ({
      ...prev,
      emails: [...prev.emails, ""],
    }));
  };

  const removeEmailField = (index: number) => {
    setEditFormData((prev) => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index),
    }));
  };

  const updateEmail = (index: number, value: string) => {
    setEditFormData((prev) => ({
      ...prev,
      emails: prev.emails.map((email, i) => (i === index ? value : email)),
    }));
  };

  const setPhoneAtIndex = (index: number, updater: (phone: PhoneFormValue) => PhoneFormValue) => {
    setEditFormData((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.map((phone, i) => (i === index ? updater(phone) : phone)),
    }));
  };

  const addPhoneField = () => {
    setEditFormData((prev) => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, createEmptyPhone()],
    }));
  };

  const removePhoneField = (index: number) => {
    setEditFormData((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index),
    }));
  };

  const updatePhoneNumberValue = (index: number, value: string) => {
    setPhoneAtIndex(index, (phone) => ({ ...phone, number: value }));
  };

  const updatePhoneTypeValue = (index: number, value: PhoneNumberType | "") => {
    setPhoneAtIndex(index, (phone) => ({ ...phone, type: value }));
  };

  const updatePhoneExtensionValue = (index: number, value: string) => {
    setPhoneAtIndex(index, (phone) => ({ ...phone, extension: value }));
  };

  const updateContactPhone = (updater: (phone: PhoneFormValue) => PhoneFormValue) => {
    setContactFormData((prev) => ({
      ...prev,
      phone: updater(prev.phone),
    }));
  };

  const updateContactPhoneNumber = (value: string) => {
    updateContactPhone((phone) => ({ ...phone, number: value }));
  };

  const updateContactPhoneType = (value: PhoneNumberType | "") => {
    updateContactPhone((phone) => ({ ...phone, type: value }));
  };

  const updateContactPhoneExtension = (value: string) => {
    updateContactPhone((phone) => ({ ...phone, extension: value }));
  };

  if (loading) {
    return (
      <div className="container mx-auto space-y-6 p-1 sm:p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="container mx-auto p-1 sm:p-6">
        <div className="mb-6 flex items-center gap-4">
          <Button onClick={() => router.history.back()} variant="ghost" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
        <Alert variant="destructive">
          <AlertDescription>{error || "Customer not found"}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const customerPhoneList: CustomerPhoneNumber[] =
    customer.phoneNumberDetails && customer.phoneNumberDetails.length > 0
      ? customer.phoneNumberDetails
          .filter((phone) => phone?.number?.trim())
          .map((phone) => ({
            number: phone.number.trim(),
            type: phone.type ?? "other",
            extension: phone.extension ? phone.extension.trim() || null : null,
          }))
      : (customer.phoneNumbers ?? [])
          .map((number) => number.trim())
          .filter(Boolean)
          .map((number) => ({
            number,
            type: "other" as PhoneNumberType,
            extension: null,
          }));

  const displayName =
    customer.name ||
    [customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
    "Unnamed Customer";

  return (
    <div className="container mx-auto space-y-6 px-4 py-6 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-4">
          <Button
            onClick={() => router.history.back()}
            variant="ghost"
            size="sm"
            className="w-full sm:w-auto"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{displayName}</h1>
              {!customer.authless && (
                <Badge
                  variant={customer.accessRevoked ? "destructive" : "secondary"}
                  className="w-fit text-xs"
                >
                  {customer.accessRevoked ? "Access Revoked" : "Has login access"}
                </Badge>
              )}
            </div>
            {customer.title && <p className="text-muted-foreground text-sm">{customer.title}</p>}
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {customer.authless && (
            <InviteUserDialog
              defaultEmail={customer.email}
              defaultRole="customer"
              defaultRoles={["customer"]}
              customerId={customer.uid}
              triggerButton={
                <Button variant="outline" className="w-full sm:w-auto">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite Customer
                </Button>
              }
            />
          )}
          {!customer.authless && (
            <Button
              onClick={handleToggleAccessRevoked}
              variant="outline"
              className={`w-full sm:w-auto ${
                customer.accessRevoked
                  ? "text-green-600 hover:bg-green-50 hover:text-green-700"
                  : "text-orange-600 hover:bg-orange-50 hover:text-orange-700"
              }`}
              title={customer.accessRevoked ? "Restore Access" : "Revoke Access"}
            >
              <Ban className="mr-2 h-4 w-4" />
              {customer.accessRevoked ? "Restore Access" : "Revoke Access"}
            </Button>
          )}
          {!customer.authless && (
            <UpdatePasswordDialog
              customerId={customerId}
              updateCustomerPassword={updateCustomerPassword}
              loading={operationLoading}
              triggerClassName="w-full sm:w-auto"
            />
          )}
          <Button onClick={openEditDialog} className="w-full sm:w-auto">
            <Edit2 className="mr-2 h-4 w-4" />
            Edit Customer
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteCustomer}
            disabled={operationLoading}
            className="w-full sm:w-auto"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Customer
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
          <TabsTrigger value="details" className="w-full">
            Customer Details
          </TabsTrigger>
          <TabsTrigger value="audit-logs" className="w-full">
            Audit Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground text-sm font-medium">Primary Email</Label>
                  {customer.email ? (
                    <a
                      href={`mailto:${customer.email}`}
                      className="block text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {customer.email}
                    </a>
                  ) : (
                    <p className="text-muted-foreground text-sm">No primary email set</p>
                  )}
                </div>

                {customer.emails &&
                  customer.emails.filter((email) => email !== customer.email).length > 0 && (
                    <div>
                      <Label className="text-muted-foreground text-sm font-medium"></Label>
                      <div className="space-y-1">
                        {customer.emails
                          .filter((email) => email !== customer.email)
                          .map((email, index) => (
                            <a
                              key={index}
                              href={`mailto:${email}`}
                              className="block text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {email}
                            </a>
                          ))}
                      </div>
                    </div>
                  )}

                {customerPhoneList.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Phone Numbers
                    </Label>
                    <div className="space-y-2">
                      {customerPhoneList.map((phone, index) => (
                        <div
                          key={`${phone.number}-${index}`}
                          className="flex flex-wrap items-center gap-2 text-sm"
                        >
                          <Phone className="h-4 w-4 text-blue-600" />
                          <a
                            href={`tel:${phone.number}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {formatPhoneNumber(phone.number)}
                          </a>
                          {phone.type && (
                            <Badge variant="outline" className="text-xs capitalize">
                              {PHONE_TYPE_LABELS[phone.type]}
                            </Badge>
                          )}
                          {phone.extension && (
                            <span className="text-muted-foreground text-xs">
                              Ext {phone.extension}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {customer.website && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">Website</Label>
                    <a
                      href={customer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {customer.website}
                    </a>
                  </div>
                )}

                {customer.bestCommunicationMethod && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Best Communication Method
                    </Label>
                    <p className="text-sm capitalize">{customer.bestCommunicationMethod}</p>
                  </div>
                )}

                {customer.title && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">Title</Label>
                    <p className="text-sm">{customer.title}</p>
                  </div>
                )}

                {(customer.firstName || customer.lastName) && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Main Contact Person
                    </Label>
                    <p className="text-sm">
                      {[customer.firstName, customer.lastName].filter(Boolean).join(" ")}
                    </p>
                  </div>
                )}

                <div>
                  <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <Label className="text-muted-foreground text-sm font-medium">Notes</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setNotesFormData(customer.notes || "");
                        setIsNotesDialogOpen(true);
                      }}
                      className="w-full sm:w-auto"
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      Edit Notes
                    </Button>
                  </div>
                  {customer.notes ? (
                    <p className="text-sm whitespace-pre-wrap">{customer.notes}</p>
                  ) : (
                    <p className="text-muted-foreground text-sm">No notes available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Account Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5" />
                  Account Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground text-sm font-medium">
                    Customer since
                  </Label>
                  <p className="text-sm">{formatDate(customer.createdAt)}</p>
                </div>

                {customer.updatedAt && customer.updatedAt !== customer.createdAt && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Last updated
                    </Label>
                    <p className="text-sm">{formatDate(customer.updatedAt)}</p>
                  </div>
                )}

                {customer.lastLogin && (
                  <div>
                    <Label className="text-muted-foreground text-sm font-medium">
                      Last Signed in
                    </Label>
                    <p className="text-sm">{formatDate(customer.lastLogin)}</p>
                  </div>
                )}

                {inviteData && !(customer.authless && inviteData.status === "accepted") && (
                  <>
                    <div className="border-t pt-4">
                      <Label className="text-muted-foreground text-sm font-medium">
                        Invitation Status
                      </Label>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={inviteData.status === "pending" ? "secondary" : "default"}
                            className="capitalize"
                          >
                            {inviteData.status}
                          </Badge>
                        </div>
                        {inviteData.invitedAt && (
                          <div>
                            <p className="text-muted-foreground text-xs">Invited on</p>
                            <p className="text-sm">{formatDate(inviteData.invitedAt)}</p>
                          </div>
                        )}
                        {inviteData.acceptedAt && (
                          <div>
                            <p className="text-muted-foreground text-xs">Invitation accepted on</p>
                            <p className="text-sm">{formatDate(inviteData.acceptedAt)}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {!customer.updatedAt &&
                  customer.updatedAt === customer.createdAt &&
                  !customer.lastLogin &&
                  !inviteData && (
                    <p className="text-muted-foreground text-sm">
                      No additional activity information available
                    </p>
                  )}
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customer.address && Object.values(customer.address).some((val) => val) ? (
                  <div className="space-y-2">
                    {customer.address.street && (
                      <p className="text-sm">{customer.address.street}</p>
                    )}
                    {customer.address.address2 && (
                      <p className="text-sm">{customer.address.address2}</p>
                    )}
                    <p className="text-sm">
                      {[customer.address.city, customer.address.state].filter(Boolean).join(", ")}
                      {customer.address.zipCode && ` ${customer.address.zipCode}`}
                    </p>
                    {customer.address.country && (
                      <p className="text-sm">{customer.address.country}</p>
                    )}
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        [
                          customer.address.street,
                          customer.address.city,
                          customer.address.state,
                          customer.address.zipCode,
                          customer.address.country,
                        ]
                          .filter(Boolean)
                          .join(", "),
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <MapPin className="h-4 w-4" />
                      Open in Google Maps
                    </a>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No address provided</p>
                )}
              </CardContent>
            </Card>

            {/* Projects */}
            <Card className="overflow-hidden md:col-span-2">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="flex items-center gap-2">Projects</CardTitle>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    {unassignedProjects.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsAssignProjectDialogOpen(true)}
                        className="w-full sm:w-auto"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Assign Existing Project
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate({
                          to: "/dashboard/admin/projects/create",
                          search: { customerId },
                        })
                      }
                      className="w-full sm:w-auto"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create New Project
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0">
                {projectsLoading ? (
                  <div className="px-6">
                    <Skeleton className="h-6 w-full" />
                  </div>
                ) : projectsError ? (
                  <div className="px-6">
                    <Alert variant="destructive">
                      <AlertDescription>{projectsError}</AlertDescription>
                    </Alert>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="w-full px-6">
                      <ProjectsTable
                        projects={projects || []}
                        customers={customerUsers}
                        managers={managerUsers}
                        admins={adminUsers}
                        agencies={agencies}
                        loading={projectsLoading}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact People */}
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Contact People
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => openContactDialog()}
                    className="w-full sm:w-auto"
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add Contact
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {customer.contacts && customer.contacts.length > 0 ? (
                    customer.contacts.map((contact) => {
                      const phoneDetails = contact.phoneDetails?.number
                        ? {
                            number: contact.phoneDetails.number.trim(),
                            type: (contact.phoneDetails.type ?? "other") as PhoneNumberType,
                            extension: contact.phoneDetails.extension
                              ? contact.phoneDetails.extension.trim() || null
                              : null,
                          }
                        : contact.phone?.trim()
                          ? {
                              number: contact.phone.trim(),
                              type: "other" as PhoneNumberType,
                              extension: null,
                            }
                          : null;

                      return (
                        <div
                          key={contact.uniqueId || contact.id}
                          className="flex flex-col gap-3 rounded-lg border p-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{contact.title}</p>
                            {contact.name && (
                              <p className="text-muted-foreground truncate text-xs">
                                {contact.name}
                              </p>
                            )}
                            <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                              {contact.email && (
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {contact.email}
                                </a>
                              )}
                              {contact.email && phoneDetails?.number && <span>-</span>}
                              {phoneDetails?.number && (
                                <>
                                  <a
                                    href={`tel:${phoneDetails.number}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {formatPhoneNumber(phoneDetails.number)}
                                  </a>
                                  {phoneDetails.type && (
                                    <Badge variant="outline" className="text-[10px] capitalize">
                                      {PHONE_TYPE_LABELS[phoneDetails.type]}
                                    </Badge>
                                  )}
                                  {phoneDetails.extension && (
                                    <span className="text-muted-foreground">
                                      Ext {phoneDetails.extension}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                            {contact.notes && (
                              <p className="text-muted-foreground truncate text-xs">
                                {contact.notes}
                              </p>
                            )}
                          </div>
                          <div className="flex w-full flex-wrap items-center gap-1 sm:w-auto sm:justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full sm:w-auto"
                              onClick={() => openContactDialog(contact)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveContact(contact.uniqueId || contact.id)}
                              disabled={operationLoading}
                              className="w-full sm:w-auto"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      No contacts yet. Add a contact to get started.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle>Attachments</CardTitle>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="attachment-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
                    />
                    <Button
                      size="sm"
                      onClick={() => document.getElementById("attachment-upload")?.click()}
                      disabled={uploadingFiles.length > 0}
                      className="w-full sm:w-auto"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload File
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {uploadingFiles.map((fileName) => (
                    <div
                      key={fileName}
                      className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3"
                    >
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      <span className="text-muted-foreground text-sm">Uploading {fileName}...</span>
                    </div>
                  ))}

                  {customer.attachments && customer.attachments.length > 0
                    ? customer.attachments.map((attachment) => (
                        <div
                          key={attachment.uniqueId || attachment.id}
                          className="flex flex-col gap-3 rounded-lg border p-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <div className="min-w-0 flex-1">
                              {attachment.fileUrl ? (
                                <a
                                  href={attachment.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="cursor-pointer truncate text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                  title="Click to open in new tab"
                                >
                                  {attachment.label}
                                </a>
                              ) : (
                                <p className="truncate text-sm font-medium">{attachment.label}</p>
                              )}
                              {attachment.fileName && (
                                <p className="text-muted-foreground truncate text-xs">
                                  {attachment.fileName}
                                </p>
                              )}
                              {attachment.uploadedAt && (
                                <p className="text-muted-foreground text-xs">
                                  Uploaded {formatDate(attachment.uploadedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex w-full flex-wrap items-center gap-1 sm:w-auto sm:justify-end">
                            {attachment.fileUrl && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(attachment.fileUrl, "_blank")}
                                className="w-full sm:w-auto"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleRemoveAttachment(attachment.id, attachment.fileUrl)
                              }
                              disabled={operationLoading}
                              className="w-full sm:w-auto"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    : uploadingFiles.length === 0 && (
                        <p className="text-muted-foreground py-4 text-center text-sm">
                          No attachments yet. Upload some files to get started.
                        </p>
                      )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="audit-logs" className="mt-6">
          <UserAuditLogs userId={customer.uid} />
        </TabsContent>
      </Tabs>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>Update customer information and contact details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCustomer}>
            <div className="grid gap-4 py-4">
              {/* Name */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-name" className="text-right">
                  Company Name
                </Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="col-span-3"
                />
              </div>

              {/* Email Addresses */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="pt-2 text-right">Email Addresses</Label>
                <div className="col-span-3 space-y-2">
                  {/* Primary Email */}
                  <div className="flex items-center gap-2">
                    <Input
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      placeholder="primary@example.com"
                      className="flex-1"
                    />
                    <Badge variant="secondary" className="shrink-0">
                      Primary
                    </Badge>
                  </div>
                  {!customer.authless && (
                    <p className="text-muted-foreground text-xs">
                      This email is linked to the customer's login account
                    </p>
                  )}
                  {/* Other Emails */}
                  {editFormData.emails.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => updateEmail(index, e.target.value)}
                        placeholder="email@example.com"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeEmailField(index)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addEmailField}>
                    Add Email
                  </Button>
                </div>
              </div>

              {/* Phone Numbers */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="pt-2 text-right">Phone Numbers</Label>
                <div className="col-span-3 space-y-2">
                  {editFormData.phoneNumbers.map((phone, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-2"
                    >
                      <Input
                        type="tel"
                        value={phone.number}
                        onChange={(e) => updatePhoneNumberValue(index, e.target.value)}
                        placeholder="(555) 123-4567"
                        className="sm:flex-1"
                      />
                      <Select
                        value={phone.type || undefined}
                        onValueChange={(value) =>
                          updatePhoneTypeValue(index, value as PhoneNumberType)
                        }
                      >
                        <SelectTrigger className="w-full sm:w-32">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PHONE_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Input
                          value={phone.extension}
                          onChange={(e) => updatePhoneExtensionValue(index, e.target.value)}
                          placeholder="Ext"
                          className="w-full sm:w-20"
                        />
                        {editFormData.phoneNumbers.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removePhoneField(index)}
                            className="shrink-0"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addPhoneField}>
                    Add Phone
                  </Button>
                </div>
              </div>

              {/* Website */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-website" className="text-right">
                  Website
                </Label>
                <Input
                  id="edit-website"
                  type="url"
                  value={editFormData.website}
                  onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                  className="col-span-3"
                  placeholder="https://example.com"
                />
              </div>

              {/* Best Communication Method */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-bestCommunicationMethod" className="text-right">
                  Best Communication
                </Label>
                <Select
                  value={editFormData.bestCommunicationMethod}
                  onValueChange={(value) =>
                    setEditFormData({
                      ...editFormData,
                      bestCommunicationMethod: value as "call" | "text" | "whatsapp" | "email",
                    })
                  }
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select preferred method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Title */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-title" className="text-right">
                  Title
                </Label>
                <Input
                  id="edit-title"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="col-span-3"
                  placeholder="Contact person's title"
                />
              </div>

              {/* First Name */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-firstName" className="text-right">
                  First Name
                </Label>
                <Input
                  id="edit-firstName"
                  value={editFormData.firstName}
                  onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                  className="col-span-3"
                  placeholder="Contact person's first name"
                />
              </div>

              {/* Last Name */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-lastName" className="text-right">
                  Last Name
                </Label>
                <Input
                  id="edit-lastName"
                  value={editFormData.lastName}
                  onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                  className="col-span-3"
                  placeholder="Contact person's last name"
                />
              </div>

              {/* Address */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="pt-2 text-right">Address</Label>
                <div className="col-span-3 space-y-2">
                  <PlacesAutocomplete
                    value={editFormData.address.street}
                    onChange={(value, meta) =>
                      setEditFormData((prev) => {
                        const addressUpdates = meta?.address ?? {};
                        return {
                          ...prev,
                          address: {
                            ...prev.address,
                            ...addressUpdates,
                            street: addressUpdates.street ?? value,
                          },
                        };
                      })
                    }
                    placeholder="Search for street address..."
                    types="geocode"
                    language="en"
                  />
                  <Input
                    placeholder="Apt, Suite, Unit, etc. (optional)"
                    value={editFormData.address.address2}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        address: {
                          ...editFormData.address,
                          address2: e.target.value,
                        },
                      })
                    }
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="City"
                      value={editFormData.address.city}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            city: e.target.value,
                          },
                        })
                      }
                    />
                    <Input
                      placeholder="State"
                      value={editFormData.address.state}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            state: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Zip Code"
                      value={editFormData.address.zipCode}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            zipCode: e.target.value,
                          },
                        })
                      }
                    />
                    <Input
                      placeholder="Country"
                      value={editFormData.address.country}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          address: {
                            ...editFormData.address,
                            country: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="edit-notes" className="pt-2 text-right">
                  Notes
                </Label>
                <Textarea
                  id="edit-notes"
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  className="col-span-3"
                  placeholder="Any additional notes about this customer..."
                  rows={3}
                />
              </div>

              {/* Password Management for Authless Customers */}
              {customer?.authless && (
                <div className="grid grid-cols-4 items-start gap-4 rounded-lg border bg-blue-50 p-4 dark:bg-blue-950/20">
                  <Label className="pt-2 text-right font-medium">Enable Login Access</Label>
                  <div className="col-span-3 space-y-3">
                    <p className="text-muted-foreground text-sm">
                      This customer is currently authless. Set a password to enable login access.
                    </p>
                    <div>
                      <Label htmlFor="password" className="text-sm">
                        New Password *
                      </Label>
                      <Input
                        id="password"
                        type="password"
                        value={editFormData.password}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            password: e.target.value,
                          })
                        }
                        placeholder="Enter password (min 6 characters)"
                        minLength={6}
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirmPassword" className="text-sm">
                        Confirm Password *
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={editFormData.confirmPassword}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            confirmPassword: e.target.value,
                          })
                        }
                        placeholder="Confirm password"
                      />
                      {editFormData.password &&
                        editFormData.confirmPassword &&
                        editFormData.password !== editFormData.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {operationError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{operationError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={operationLoading}>
                {operationLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Customer"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{contactFormData.id ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <DialogDescription>
              {contactFormData.id
                ? "Update the contact person's details."
                : "Add a new contact person for this customer."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveContact}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-title" className="text-right">
                  Title
                </Label>
                <Input
                  id="contact-title"
                  value={contactFormData.title}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      title: e.target.value,
                    })
                  }
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-name" className="text-right">
                  Name
                </Label>
                <Input
                  id="contact-name"
                  value={contactFormData.name}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      name: e.target.value,
                    })
                  }
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-email" className="text-right">
                  Email
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactFormData.email}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      email: e.target.value,
                    })
                  }
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="contact-phone" className="pt-2 text-right">
                  Phone
                </Label>
                <div className="col-span-3 space-y-2">
                  <Input
                    id="contact-phone"
                    value={contactFormData.phone.number}
                    onChange={(e) => updateContactPhoneNumber(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-2">
                    <Select
                      value={contactFormData.phone.type || undefined}
                      onValueChange={(value) => updateContactPhoneType(value as PhoneNumberType)}
                    >
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PHONE_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={contactFormData.phone.extension}
                      onChange={(e) => updateContactPhoneExtension(e.target.value)}
                      placeholder="Ext"
                      className="w-full sm:w-24"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="contact-notes" className="text-right">
                  Notes
                </Label>
                <Textarea
                  id="contact-notes"
                  value={contactFormData.notes}
                  onChange={(e) =>
                    setContactFormData({
                      ...contactFormData,
                      notes: e.target.value,
                    })
                  }
                  className="col-span-3"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={operationLoading}>
                {operationLoading ? "Saving..." : "Save Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Assign Existing Project Dialog */}
      <Dialog open={isAssignProjectDialogOpen} onOpenChange={setIsAssignProjectDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Assign Existing Project</DialogTitle>
            <DialogDescription>
              Select a project to assign to {displayName}. These projects are not currently assigned
              to any customer.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            <div className="space-y-3">
              {unassignedProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex flex-col gap-3 rounded-lg border p-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{project.name}</p>
                    {project.description && (
                      <p className="text-muted-foreground truncate text-xs">
                        {project.description}
                      </p>
                    )}
                    {getJobLocationAddress(project.jobLocation) && (
                      <p className="text-muted-foreground flex items-center gap-1 truncate text-xs">
                        <MapPin className="h-3 w-3" />
                        {getJobLocationAddress(project.jobLocation)}
                      </p>
                    )}
                    {getJobLocationNotes(project.jobLocation) && (
                      <p className="text-muted-foreground flex items-center gap-1 truncate text-xs">
                        <FileText className="h-3 w-3" />
                        {getJobLocationNotes(project.jobLocation)}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      handleAssignProject(project.id, project.name);
                      setIsAssignProjectDialogOpen(false);
                    }}
                    disabled={operationLoading || updating}
                    className="w-full sm:w-auto"
                  >
                    Assign to Customer
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Notes Dialog */}
      <Dialog open={isNotesDialogOpen} onOpenChange={setIsNotesDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Customer Notes</DialogTitle>
            <DialogDescription>Update notes for {displayName}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveNotes}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={notesFormData}
                  onChange={(e) => setNotesFormData(e.target.value)}
                  placeholder="Add any notes about this customer..."
                  rows={6}
                  className="resize-none"
                />
              </div>
            </div>

            {operationError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{operationError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsNotesDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={operationLoading}>
                {operationLoading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Notes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
